/*
 * @format
 */
import * as strings from './utils/strings';

import { isMatchingZoneId, sortHomesById } from './utils/helpers';

import { MODES } from './utils/consts';

import {
  getZone,
  getSetpoint,
  getMode,
  getFrostProtectionSetpoint,
  getPendingErrors,
  isAutoOff,
  isCloudSupported,
  canSettingsBeSent,
} from './controls-selectors';
import {
  getNotificationColour,
  getNotificationRoute,
} from './utils/notifications';

export const getLastEmail = state => state.auth.email;

export const getPassword = state => state.authSensitive.password;

export const isAuthenticated = state => state.auth.isAuthenticated === true;

export const hasReceivedHelloMessage = state =>
  state.auth.helloMessageReceived === true;

export const isDemoMode = state => state.auth.demoMode === true;

export const isDemoUnavailableVisible = state =>
  state.auth.demoUnavailableVisible === true;

export const hasHelloMessageTimedOut = state =>
  state.auth.helloMessageTimeout === true;

export const isLoggingOut = state => state.auth.isLoggingOut === true;

export const getAcceptedMarketing = state => state.auth.AcceptedMarketing;

export const getAcceptedTerms = state => state.auth.AcceptedTerms;

export const getTermsAgreedAt = state => state.auth.TermsAgreedAt;

export const getTermsVersion = state => state.auth.TermsVersion;

export const getTermsAndConditionsCompleted = state =>
  state.auth.termsAndConditionsCompleted;

export const getDeviceGeoStatus = state => state.deviceGeoStatus;

export const getAwaitingZoneStatus = state =>
  state.awaitingZoneStatus.requestState;

export const getCurrentEmail = state => {
  if (isAuthenticated(state)) {
    return state.auth.email;
  }
  return '';
};

export const getDeviceRegistrationAttempts = state =>
  state.deviceRegistration.registrationAttempts;

export const getDeviceRegistrationSerialNumber = state =>
  state.deviceRegistration.serialNumber;

export const getDeviceRegistrationPin = state => state.deviceRegistration.pin;

export const getDeviceRegistrationError = state =>
  state.deviceRegistration.deviceRegistrationError;

export const getDeviceRegistrationErrorTitle = state =>
  state.deviceRegistration.errorTitle;

export const getDeviceRegistrationSuccess = state =>
  state.deviceRegistration.deviceRegistrationSucceeded;

export const getDeviceRegistrationInProgress = state =>
  state.deviceRegistration.deviceRegistrationInProgress;

export const getGatewayList = state => state.gatewayManagement.gateways;

export const getGatewayListCallStatus = state => ({
  inProgress: state.gatewayManagement.listAllDevicesInProgress,
  succeeded: state.gatewayManagement.listAllDevicesSucceeded,
  failed:
    (state.gatewayManagement.listAllDevicesInProgress ||
      state.gatewayManagement.listAllDevicesSucceeded) === false,
  errorMessage: state.gatewayManagement.listAllDevicesErrorMessage,
});

export const getDefaultHomeId = state => {
  if (state.homeList.homes.length > 0) {
    // copy before sorting so we don't mutate the redux state array in place
    return [...state.homeList.homes].sort(sortHomesById)[0].id;
  }

  return null;
};

// look for other devices in homes that we can copy to
// they're eligable to copy if they're the same endpoint
export const getOtherDevicesSameType = (
  state,
  homeId,
  zoneId,
  options = { excludeThisDevice: false, ownerRoleOnly: false },
) => {
  const { excludeThisDevice, ownerRoleOnly } = options;
  const { homes } = state.homeList;
  const targetZoneType = getZoneType(state, homeId, zoneId);

  if (homes) {
    let devices = homes;

    devices = devices
      .map(home => {
        const zone = home.zones.filter(thisZone => {
          if (thisZone.type === targetZoneType) {
            return true;
          }

          return false;
        });
        return {
          devices: zone,
          homeName: home.friendlyName,
          homeId: home.id,
          role: home.role,
        };
      })
      .map(home => {
        return home.devices.map(device => {
          // if name is just heating, add home name as well
          const name = `${home.homeName} - ${device.friendlyName}`;
          return {
            ...device,
            friendlyName: name,
            homeId: home.homeId,
            role: home.role,
            settingsLocked: !canSettingsBeSent(
              state,
              home.homeId,
              device.zoneId,
            ),
          };
        });
      })
      .flat(1);

    if (excludeThisDevice || ownerRoleOnly) {
      devices = devices.filter(device => {
        if (excludeThisDevice) {
          const shouldExcludeThisDevice =
            device.homeId === homeId && device.zoneId[0] === zoneId[0];

          if (shouldExcludeThisDevice) {
            return false;
          }
        }

        if (ownerRoleOnly) {
          const deviceDoesNotHaveOwnerRole = device.role !== 'Owner';

          if (deviceDoesNotHaveOwnerRole) {
            return false;
          }
        }

        return true;
      });
    }

    return devices;
  }
  return null;
};

// Homes sorted by id, zones passed through untouched.
//
// This historically overlaid local optimistic pending (setpoint/mode/holidayMode)
// onto each zone, but no consumer ever read those overlaid fields — the app's
// optimistic control render goes through the fine-grained getMode/getSetpoint —
// so that dead overlay was removed. The sort is load-bearing: several screens
// render this list in order.
export const getSortedHomes = state => {
  return state.homeList.homes.map(home => ({ ...home })).sort(sortHomesById);
};

export const getHomeById = (state, homeId) => {
  return state.homeList.homes.find(x => x.id === homeId);
};

export const getIsHaloAir = (state, homeId) => {
  const home = state?.homeList?.homes.find(x => x.id === homeId);

  return home?.type === 'Halo Air';
};

export const getIsLogicDevice = (state, homeId) => {
  const home = state?.homeList?.homes.find(x => x.id === homeId);

  return home?.type?.length > 0 ? home?.type.startsWith('Logic') : false;
};

export function getInitialHomeAndZone(state, lastHome) {
  if (state.homeList) {
    const { homes } = state.homeList;
    // copy so the .sort() below doesn't mutate the redux state array in place
    const allowedHomes = [...homes];

    // this double checks that we can access
    if (lastHome?.homeId) {
      const matchingHome = allowedHomes.find(h => h.id === lastHome.homeId);

      if (matchingHome) {
        return lastHome;
      }
    }

    const home = allowedHomes
      .sort((a, b) =>
        a.deviceRegistrationId.localeCompare(b.deviceRegistrationId),
      )
      .find(
        h =>
          ['Logic', 'Logic Heat Pump'].includes(h.type) || h.zones.length > 0,
      );

    if (home) {
      return { homeId: home.id, zoneId: home.zones[0]?.zoneId };
    }
  }
  return null;
}

export const getHomeByDeviceRegistrationId = (state, deviceRegistrationId) => {
  return state.homeList.homes.find(
    x => x.deviceRegistrationId === deviceRegistrationId,
  );
};

export const getIsHomeAwaitingZones = (state, home) => {
  if (home && home.id) {
    const homeRetrieved = state.homeList.homes.find(x => x.id === home.id);
    return homeRetrieved.awaitingZones;
  }
  return true;
};

export const getHomeNames = (state, homeIdToExclude) => {
  let excludeHomeId;
  if (homeIdToExclude) {
    // if set, we'll exclude a provided home ID
    // this is used during initial set-up
    excludeHomeId = homeIdToExclude.toLowerCase().replace(/:/g, '');
  } else if (state.homeList && state.homeList.selectedHome) {
    // if homesToExclude not set, we'll exclude the current home
    // this means a user is allowed to name a current device the same on the rename screens
    excludeHomeId = state.homeList.selectedHome;
  }
  if (state.homeList.homes) {
    return state.homeList.homes
      .filter(x => {
        return x.id !== excludeHomeId;
      })
      .map(x => x.friendlyName);
  }
  return [];
};

export const getOwnedHomes = state => {
  if (state.homeList.homes) {
    const homes = state.homeList.homes
      .map(x => {
        return {
          homeId: x.id,
          deviceRegistrationId: x.deviceRegistrationId,
          friendlyName: x.friendlyName,
          role: x.role,
          type: x.type,
        };
      })
      .filter(x => {
        return x.role === 'Owner';
      });

    return homes;
  }

  return [];
};

export const getOwnedHaloHomes = state => {
  const ownedHomes = getOwnedHomes(state);

  if (ownedHomes.length === 0) {
    return [];
  }

  return ownedHomes.filter(home => home?.type?.startsWith('Halo'));
};

export const getHomeFriendlyName = (state, homeId) => {
  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home || !home.friendlyName) {
    return homeId;
  }

  return home.friendlyName;
};

export const getHomeDeviceSerialNumber = (state, homeId) => {
  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home || !home.serialNumber) {
    return null;
  }

  return home.serialNumber;
};

export const getHomeDeviceRegistrationId = (state, homeId) => {
  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return homeId;
  }

  return home.deviceRegistrationId;
};

export const wasFirstHelloEmpty = state => {
  const { firstHelloWasEmpty } = state.homeList;
  return !!firstHelloWasEmpty;
};

export const getCurrentTemperature = (state, homeId, zoneId) => {
  const home = state.homeList.homes.find(x => x.id === homeId);
  const zone = home.zones.find(x => isMatchingZoneId(x.zoneId, zoneId));
  return zone ? zone.temperature : null;
};

export const isPlusHoursActive = (state, prtId) => {
  const device = state.zone.find(x => x.prtId === prtId);
  return device ? device.mode === 'plusHours' : false;
};

export const getDeviceList = state => ({
  devices: state.homeList.smartInterfaces,
});

export const previousHome = state => {
  const { homes, selectedHome } = state.homeList;

  let indexOfSelected = homes.findIndex(x => x.id === selectedHome);

  if (indexOfSelected === -1) {
    return 'No smart interfaces';
  }

  if (indexOfSelected === 0) {
    indexOfSelected = homes.length;
  }

  indexOfSelected -= 1;

  const previousDeviceSNInArray = homes[indexOfSelected].id;

  return previousDeviceSNInArray;
};

export const nextHome = state => {
  const { homes, selectedHome } = state.homeList;

  let indexOfSelected = homes.findIndex(x => x.id === selectedHome);

  if (indexOfSelected === -1) {
    return 'No smart interfaces';
  }

  indexOfSelected += 1;
  indexOfSelected %= homes.length;

  const nextDeviceSNInArray = homes[indexOfSelected].id;

  return nextDeviceSNInArray;
};

/*
export const showPlusHoursModal = state =>
  state.pendingPrt.showPlusHoursModal === true;
  */

export const getScheduleUpdateState = (state, requestId) => {
  if (!state.schedule.scheduleRequest) {
    return 'None';
  }

  if (
    requestId !== undefined &&
    state.schedule.scheduleRequest.requestId !== requestId
  ) {
    return 'None';
  }

  return state.schedule.scheduleRequest
    ? state.schedule.scheduleRequest.state
    : 'None';
};

export const getScheduleUpdateInProgress = (state, requestId) => {
  if (!state.schedule.scheduleRequest) {
    return false;
  }

  if (
    requestId !== undefined &&
    state.schedule.scheduleRequest.requestId !== requestId
  ) {
    return false;
  }

  return state.schedule.scheduleRequest?.state !== undefined;
};

export const getScheduleUpdateError = (state, requestId) => {
  if (!state.schedule.scheduleRequest) {
    return '';
  }

  if (
    requestId !== undefined &&
    state.schedule.scheduleRequest.requestId !== requestId
  ) {
    return '';
  }

  if (state.schedule.scheduleRequest.state === 'Timed out') {
    return 'The request to update your schedule has timed out. Please try again.';
  }

  if (state.schedule.scheduleRequest.state === 'Rejected') {
    return 'The request to update your schedule was rejected. Please try again.';
  }

  return '';
};

export const getSchedulesBatchUpdateState = state =>
  state.schedule.batchRequest ? state.schedule.batchRequest.state : 'None';

export const getSchedulesBatchUpdateInProgress = state =>
  state.schedule.batchRequest?.state !== undefined;

export const getSchedulesBatchUpdateError = state => {
  if (!state.schedule.batchRequest) {
    return '';
  }

  if (state.schedule.batchRequest.state === 'Timed out') {
    return 'The request to update your schedule has timed out. Please try again.';
  }

  if (state.schedule.batchRequest.state === 'Rejected') {
    return 'The request to update your schedule was rejected. Please try again.';
  }

  return '';
};

export const getZones = (state, homeId) => {
  if (!homeId) {
    return [];
  }
  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return [];
  }

  return home.zones || [];
};

export const getIfMultiZone = (state, homeId) => {
  const zones = getZones(state, homeId);

  return zones.length > 1;
};

export const getChZones = (state, homeId) => {
  const zones = getZones(state, homeId);

  return zones.filter(x => x.type === 'CH' || x.type === 'HPI_CH');
};

export const getFirstZoneId = (state, homeId) => {
  if (!homeId) {
    return null;
  }
  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return null;
  }

  return home.zones.length > 0 ? home.zones[0].zoneId : null;
};

export const getSWC = (state, homeId) => {
  const home = getHomeById(state, homeId);
  if (home) {
    if (home.swc) {
      return home.swc;
    }
  }

  return null;
};

export const getFailsafeMode = (state, homeId) => {
  const home = getHomeById(state, homeId);

  if (home) {
    const iff = home.hpi || home.si;
    return iff?.inFailsafe;
  }
};

export const getFailsafeModeText = (state, homeId) => {
  const home = getHomeById(state, homeId);

  if (home) {
    const allPrtsConnected = isAllZonesOnline(state, homeId);
    const inFailsafeMode = getFailsafeMode(state, homeId);

    const iff = home?.hpi || home?.si;

    const failsafeManual =
      !allPrtsConnected && (iff.userOverridenOff || iff.userOverridenOn);
    const failsafeAuto = !allPrtsConnected && inFailsafeMode;

    if (failsafeManual) {
      return 'Active - Manual control';
    }

    if (failsafeAuto) {
      return 'Active - Auto';
    }

    return inFailsafeMode ? 'Active' : 'Inactive';
  }
};

export const getZigbeeRepeaters = (state, homeId) => {
  const home = getHomeById(state, homeId);
  if (home) {
    if (home.zigbeeRepeaters) {
      return home.zigbeeRepeaters;
    }
  }

  return [];
};

export const getZigbeeRepeaterById = (state, homeId, deviceId) => {
  const zigbeeRepeaters = getZigbeeRepeaters(state, homeId);

  return zigbeeRepeaters.find(x => x.deviceId === deviceId);
};

export const getInterfaceConnectionStatus = (state, homeId) => {
  let interfaceConnected = false;

  if (!homeId) {
    return interfaceConnected;
  }

  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return interfaceConnected;
  }

  interfaceConnected = home?.si?.connected || home?.hpi?.connected;

  // if we didn't receive a si or hpi part of the message or the messaging is missing the return parameter
  // we will assume there is not connection with SI or heat pump
  if (interfaceConnected === undefined) {
    return false;
  }

  return interfaceConnected;
};

export const isZoneOnline = (state, homeId, zoneId) => {
  const isPrtConnected = getPrtConnectionStatus(state, homeId, zoneId);
  const isInterfaceConnected = getInterfaceConnectionStatus(
    state,
    homeId,
    zoneId,
  );
  return isPrtConnected && isInterfaceConnected;
};

export const isAllZonesOnline = (state, homeId) => {
  const isInterfaceConnected = getInterfaceConnectionStatus(state, homeId);

  if (!isInterfaceConnected) {
    return false;
  }

  let connectionStatus = isInterfaceConnected;

  const zones = getZones(state, homeId);

  zones.forEach(item => {
    const zoneConnectionStatus = getPrtConnectionStatus(
      state,
      homeId,
      item.zoneId,
    );
    connectionStatus = connectionStatus && zoneConnectionStatus;
  });

  return connectionStatus;
};

export const oneOrMoreZonesOnline = (state, homeId) => {
  const zones = getZones(state, homeId);

  for (let i = 0; i < zones.length; i++) {
    const zone = zones[i];

    const zoneConnectionStatus = getPrtConnectionStatus(
      state,
      homeId,
      zone.zoneId,
    );

    if (zoneConnectionStatus === true) {
      return true;
    }
  }

  return false;
};

export const getStatusRowText = (state, homeId, zoneId, isInHolidayMode) => {
  const mode = getMode(state, homeId, zoneId);
  const setpoint = getSetpoint(state, homeId, zoneId);
  const zone = getZone(state, homeId, zoneId);
  const isSiConnected = getInterfaceConnectionStatus(state, homeId);
  if (!zone.connected || !isSiConnected) {
    return 'HALO';
  }
  if (
    mode === MODES.Off ||
    isInHolidayMode ||
    setpoint <= getFrostProtectionSetpoint(state, homeId, zoneId)
  ) {
    return 'Frost Protection';
  }

  return 'Target Temperature';
};

export const getMinorStatusRowText = (
  state,
  homeId,
  zoneId,
  isInHolidayMode,
  nextEventAt,
) => {
  const mode = getMode(state, homeId, zoneId);
  const setpoint = getSetpoint(state, homeId, zoneId);
  const zone = getZone(state, homeId, zoneId);
  const isSiConnected = getInterfaceConnectionStatus(state, homeId);
  if (!zone.connected || !isSiConnected) {
    return '';
  }

  const autoOff = isAutoOff(state, homeId, zoneId, isInHolidayMode);

  if (
    !autoOff &&
    (mode === MODES.Off ||
      isInHolidayMode ||
      setpoint <= getFrostProtectionSetpoint(state, homeId, zoneId))
  ) {
    return 'Active';
  }

  if (mode === MODES.Manual) {
    return 'set to';
  }

  return nextEventAt ? `Until ${nextEventAt}` : '';
};

export const getSiRegistrationState = state => {
  return state.siRegistration.registrationStatus;
};

export const getSiRegistrationHeatType = state => {
  return state.siRegistration.heatType;
};

export const getoptimisedStartEnabled = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);

  return zone.optimisedStartEnabled;
};

export const getoptimisedStopEnabled = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);

  return zone.optimisedStopEnabled;
};

export const getdelayedStartEnabled = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);

  return zone.delayedStartEnabled;
};

export const getDisplayToleranceDot1 = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);

  return zone.displayToleranceDot1;
};

export const getTimedDhw = (state, homeId, zoneId) => {
  // So...
  // The message spec for HPIs has put the timedDhw setting on the PRT Live message
  // from a Central Heating device. This makes life difficult and this seems to be
  // the least messy way to use the partial zone to find it

  const chZone = getChZoneForDhwZone(state, homeId, zoneId);

  return chZone?.timedDhw || false;
};

export const getDhwEmergencyOperation = (state, homeId) => {
  const home = getHomeById(state, homeId);
  return home?.hpi?.dhwEmergencyOperation || false;
};

export const getOptimisedStartMaxTime = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);

  return zone.optimisedStartMaxTime;
};

export const getOptimisedStopMaxTime = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);

  return zone.optimisedStopMaxTime;
};

export const getChildLockEnabled = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);
  return zone.childLock;
};

export const getIdentifyModeEnabled = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);
  return zone?.identifyModeActive;
};

export const getWeatherCompensationEnabled = (state, homeId) => {
  const home = getHomeById(state, homeId);
  if (home !== null && home !== undefined) {
    if ('weatherCompensationEnabled' in home) {
      return home.weatherCompensationEnabled;
    }
  }
  return true;
};

export const getWeatherCompensationUserEnabled = (state, homeId) => {
  const home = getHomeById(state, homeId);
  if (home !== null && home !== undefined) {
    if ('weatherCompensationUserEnabled' in home) {
      return home.weatherCompensationUserEnabled;
    }
  }
  return true;
};

export const getHeatCurveEnabled = (state, homeId) => {
  const home = getHomeById(state, homeId);
  if (home !== null && home !== undefined && home.si) {
    if ('heatCurveEnabled' in home.si) {
      return home.si.heatCurveEnabled;
    }
  }
  return true;
};

export const getHeatCurveNumber = (state, homeId) => {
  const home = getHomeById(state, homeId);
  if (home !== null && home !== undefined && home.si) {
    if ('heatCurveNumber' in home.si) {
      return home.si.heatCurveNumber;
    }
  }
  return null;
};

export const getHeatCurveNominalTemp = (state, homeId) => {
  const home = getHomeById(state, homeId);
  if (home !== null && home !== undefined && home.si) {
    if ('heatCurveNominalTemp' in home.si) {
      return home.si.heatCurveNominalTemp;
    }
  }
  return null;
};

export const getTemperatureOffset = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);
  return zone.temperatureOffset / 10;
};

export const getTimeFormat = (state, homeId, zoneId) => {
  const zones = getChZones(state, homeId);
  // then get by the physic device id (ignoring endpointId)
  const chZone = zones.find(x => x.zoneId[0] === zoneId[0]);

  return chZone?.timeFormat || false;
};

export const getTimeDisplayFormat = (state, homeId, zoneId) => {
  const is24HourFormat = getTimeFormat(state, homeId, zoneId);

  return is24HourFormat ? 'HH:mm' : 'h:mma';
};

export const getAllPrtSettings = (state, homeId, zoneId) => {
  let settings = null;

  const optimisedStopEndabled = getoptimisedStopEnabled(state, homeId, zoneId);

  const optimisedStartEnabled = getoptimisedStartEnabled(state, homeId, zoneId);

  const delayedStartEnabled = getdelayedStartEnabled(state, homeId, zoneId);

  const childLockOn = getChildLockEnabled(state, homeId, zoneId);

  const frostProtectionSetpoint = getFrostProtectionSetpoint(
    state,
    homeId,
    zoneId,
  );

  // we need to convert temperatureOffset from float to whole number as si dont support float.
  const temperatureOffset = getTemperatureOffset(state, homeId, zoneId) * 10;

  const displayToleranceDot1 = getDisplayToleranceDot1(state, homeId, zoneId);

  const timeFormat = getTimeFormat(state, homeId, zoneId);

  settings = {
    optimisedStartEnabled,
    optimisedStopEndabled,
    delayedStartEnabled,
    childLockOn,
    frostProtectionSetpoint,
    temperatureOffset,
    displayToleranceDot1,
    timeFormat,
  };

  return settings;
};

export const getInstallerContactNumber = (state, homeId, zoneId) => {
  const zones = zoneId
    ? [getZone(state, homeId, zoneId)]
    : getZones(state, homeId);

  for (let i = 0; i < zones.length; i++) {
    const zone = zones[i];

    return zone?.installerContactNumber;
  }
};

export const getPrtRequestStatus = state => {
  return state.pendingCustomReducer;
};

export const getModelIdentifier = (state, homeId) => {
  let modelIdentifier = '';

  if (!homeId) {
    return modelIdentifier;
  }

  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return modelIdentifier;
  }

  const zones = getZones(state, homeId);

  if (!zones || zones.length <= 0 || !zones[0]) {
    return modelIdentifier;
  }

  modelIdentifier = zones[0].modelIdentifier;

  return modelIdentifier;
};

export const getBoilerType = (state, homeId) => {
  const boilerType = '';

  if (!homeId) {
    return boilerType;
  }

  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return boilerType;
  }

  if (!home.boiler || !home.boiler.boilerType) {
    return boilerType;
  }

  return home.boiler.boilerType;
};

export const getCapacity = (state, homeId) => {
  const capacity = '';

  if (!homeId) {
    return capacity;
  }

  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return capacity;
  }

  if (!home.boiler || !home.boiler.capacity) {
    return capacity;
  }

  return home.boiler.capacity;
};

export const getBoilerStatus = (state, homeId) => {
  const disconnected = {
    isConnected: false,
    isInFault: false,
  };

  if (!homeId) {
    return disconnected;
  }

  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return disconnected;
  }

  if (!home.boiler) {
    return disconnected;
  }

  return {
    isConnected: !!home.boiler.openThermCommunication,
    isInFault: !!(home.boiler.faults && home.boiler.faults.length > 0),
  };
};

export const getSIProperty = (state, homeId, property, returnActive) => {
  const home = state.homeList.homes.find(x => x.id === homeId);
  if (home) {
    const homeProperty = home.si[property];
    // Returns a string for boolean outputs from selector if enabled, saves converting every one each time
    if (returnActive) {
      const boolProperty = Boolean(homeProperty);
      return boolProperty ? 'Active' : 'Inactive';
    }
    return homeProperty || '';
  }
  return '';
};

export const getOpenThermId003 = (state, homeId) => {
  const id003 = '';

  if (!homeId) {
    return id003;
  }

  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return id003;
  }

  if (!home.boiler || !home.boiler.id003) {
    return id003;
  }

  return home.boiler.id003;
};

export const getOpenThermId011 = (state, homeId) => {
  const id011 = '';

  if (!homeId) {
    return id011;
  }

  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return id011;
  }

  if (!home.boiler || !home.boiler.id011 || !Array.isArray(home.boiler.id011)) {
    return id011;
  }

  const allArrayValuesAreNullOrEmpty = home.boiler.id011.every(
    tspItem => !tspItem || typeof tspItem === 'object',
  );

  if (allArrayValuesAreNullOrEmpty) {
    return id011;
  }

  return home.boiler.id011;
};

export const getOpenThermId125 = (state, homeId) => {
  const id125 = '';

  if (!homeId) {
    return id125;
  }

  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return id125;
  }

  if (!home.boiler || !home.boiler.id125) {
    return id125;
  }

  return home.boiler.id125;
};

export const getOpenThermId127 = (state, homeId) => {
  const id127 = '';

  if (!homeId) {
    return id127;
  }

  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return id127;
  }

  if (!home.boiler || !home.boiler.id127) {
    return id127;
  }

  return home.boiler.id127;
};

export const getSiConnectionStrength = (state, homeId) => {
  let siConnectionStrength = 'poor';

  if (!homeId) {
    return siConnectionStrength;
  }

  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return siConnectionStrength;
  }

  if (!home.si || !home.si.connectionStrength) {
    return siConnectionStrength;
  }

  siConnectionStrength = home.si.connectionStrength;

  return siConnectionStrength;
};

export const getPrtFriendlyName = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);

  if (!zone || !zone.friendlyName) {
    return '';
  }

  return zone.friendlyName;
};

export const getPrtConnectionStatus = (state, homeId, zoneId) => {
  const zones = getChZones(state, homeId);

  // then get by the physic device id (ignoring endpointId)
  const chZone = zones.find(x => x.zoneId[0] === zoneId[0]);

  if (
    !chZone ||
    chZone.connectionStatus === undefined ||
    chZone.connectionStatus === false
  ) {
    return false;
  }

  return chZone.connectionStatus;
};

export const getPrtConnectionStrength = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);

  if (!zone || !zone.connectionStrength) {
    return 'poor';
  }

  return zone.connectionStrength;
};

export const getPrtBatteryLevel = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);

  if (!zone || !zone.batteryLevel) {
    return 'Low';
  }

  return zone.batteryLevel === 'high' ? 'Good' : 'Low';
};

export const getFaults = (state, homeId) => {
  if (!homeId) {
    return [];
  }
  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return [];
  }

  const faults =
    home?.boiler.faults || home?.hpi?.faults || home?.logic?.faults;

  if (!Array.isArray(faults)) {
    return [];
  }

  return faults;
};

export const getErrorNotifications = state => {
  const errors = getPendingErrors(state);

  return errors.map(x => ({
    requestId: x.requestId,
    type: 'error',
    homeId: x.homeId,
    raised: x.raised,
    message: x.message,
  }));
};

export const getNotifications = state => {
  return state.notifications.notifications.map(x => ({
    requestId: x.requestId,
    type: x.type,
    homeId: x.homeId,
    raised: x.raised,
    message: x.title,
  }));
};

export const getDeviceToken = state => {
  if (!state || !state.notifications) {
    return null;
  }

  if (!state.notifications.deviceToken) {
    return null;
  }

  return state.notifications.deviceToken;
};

// ${platform}_${deviceToken}
// ios_abcd123-asdasjh-z123
export const getPlatformDeviceToken = (state, platform) => {
  const deviceToken = getDeviceToken(state);

  if (!deviceToken) {
    return undefined;
  }

  return `${platform}_${deviceToken}`;
};

export const getToasts = state => {
  const errorNotifications = getErrorNotifications(state);
  const notifications = getNotifications(state);
  const combinedNotifications = errorNotifications
    .concat(notifications)
    .map(x => ({
      ...x,
      route: getNotificationRoute(x.type),
      colour: getNotificationColour(x.type),
    }));

  return combinedNotifications;
};

export const getControlFaults = (state, homeId) => {
  if (!homeId) {
    return [];
  }

  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return [];
  }

  if (!home.controlFaults || !Array.isArray(home.controlFaults)) {
    return [];
  }

  const getFaultCode = type => {
    switch (type) {
      // hide fault code for low battery fault
      case 'LowBattery': {
        return undefined;
      }

      default: {
        return type;
      }
    }
  };

  const isMultiZone = (home.zones || []).length > 0;

  return home.controlFaults.map(x => {
    const enriched = {
      ...x,
      faultCode: getFaultCode(x.type, x.subType),
      gasSafeEngineerRequired: false,
    };

    if (x.type === 'HaloServiceDue' && x.zoneId) {
      const zone = (home.zones || []).find(z =>
        isMatchingZoneId(z.zoneId, x.zoneId),
      );
      if (isMultiZone && zone?.friendlyName) {
        enriched.zoneFriendlyName = zone.friendlyName;
      }

      switch (x.subType) {
        case 'DueSoon': {
          const days = zone?.serviceModeDaysRemaining;
          const dayLabel = days === 1 ? 'day' : 'days';

          enriched.faultCode =
            days != null ? `Service due in ${days} ${dayLabel}` : 'Service due';
          break;
        }
        case 'Overdue':
          enriched.faultCode = 'Service due';
          break;
        case 'OverdueSetpointLimited': {
          const setpoint = zone?.serviceModeSetpoint;
          if (setpoint != null) {
            enriched.serviceModeSetpoint = setpoint;
          }
          enriched.faultCode = 'Service overdue';
          break;
        }
        default:
          enriched.faultCode = 'Service due';
          break;
      }
    }

    return enriched;
  });
};

export const getAllAlarms = (state, homeId) => {
  if (!homeId) {
    return [];
  }

  const faults = getFaults(state, homeId);
  const controlFaults = getControlFaults(state, homeId);

  if (!faults || !controlFaults) {
    return [];
  }

  return controlFaults.concat(faults);
};

export const getPrtFaults = (state, homeId, zoneId) => {
  if (!homeId) {
    return [];
  }

  const controlFaults = getControlFaults(state, homeId);

  const prtControlFaults = controlFaults.filter(
    x => x.zoneId && isMatchingZoneId(x.zoneId, zoneId),
  );

  return prtControlFaults;
};

export const getSiFaults = (state, homeId) => {
  if (!homeId) {
    return [];
  }
  let controlFaults = getControlFaults(state, homeId);
  controlFaults = controlFaults.filter(
    x => x.faultCode && x.faultCode.toLowerCase() === 'cf04',
  );
  return controlFaults;
};

export const getSwcFaults = (state, homeId) => {
  if (!homeId) {
    return [];
  }
  let controlFaults = getControlFaults(state, homeId);
  controlFaults = controlFaults.filter(
    x => x.faultCode && x.faultCode.toLowerCase() === 'cf02',
  );
  return controlFaults;
};

export function getAlarm(state, homeId, type, code, raisedAt) {
  if (!homeId) {
    return [];
  }

  const allFaults = getAllAlarms(state, homeId);

  if (!allFaults) {
    return [];
  }

  const likelyMatch = allFaults.find(
    f =>
      (f.faultCode === undefined || f.faultCode === code) &&
      f.raisedAt === raisedAt,
  );

  return likelyMatch;
}

export const getAlarmsBySeverity = (state, homeId) => {
  if (!homeId) {
    return [];
  }

  const allFaults = getAllAlarms(state, homeId);

  if (!allFaults) {
    return [];
  }

  return allFaults.sort((faultA, faultB) => {
    const faultSeverities = {
      Critical: 0,
      Warning: 1,
      Information: 2,
    };

    return faultSeverities[faultA.severity] - faultSeverities[faultB.severity];
  });
};

export const getSystemStatusSeverity = (state, homeId) => {
  if (!homeId) {
    return [];
  }

  const faults = getAlarmsBySeverity(state, homeId);

  if (faults && faults.length > 0) {
    return faults[0].severity;
  }

  return undefined;
};

export const errorString = (state, homeId) => {
  const cloudDisabled = isCloudSupported(state, homeId);
  if (cloudDisabled) {
    return strings.SETTINGS_DISABLED_BODY;
  }
  return strings.IDEAL_CLOUD_LICENCE_NOT_PRESENT;
};

export const checkForResumeRegistationFlag = state => {
  return state.resumeRegistration.state === 'notStarted';
};

export const showOfflineResumeRegistration = state => {
  return state.resumeRegistration.state === 'offline';
};

export const showOnlineResumeRegistration = state => {
  return (
    state.resumeRegistration.macAddress &&
    state.resumeRegistration.publicKey &&
    state.resumeRegistration.state === 'online'
  );
};

export const resumeRegistrationDetails = state => {
  const { macAddress, publicKey, connectionValidAfter } =
    state.resumeRegistration;
  return {
    macAddress,
    publicKey,
    connectionValidAfter,
  };
};

export const getRole = (state, homeId) => {
  if (!homeId) {
    return '';
  }

  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return '';
  }

  const { role } = home;

  return role;
};

export const getIsSettingRequestInProgress = (state, requestId) => {
  // this allows screens to call this func before they've started a request
  if (requestId === undefined) {
    return false;
  }
  const inProgress = state.settings.current?.request?.requestId === requestId;
  return inProgress;
};

export const canSettingsBeSentToHome = (state, homeId) => {
  const cloudSupported = isCloudSupported(state, homeId);
  if (!cloudSupported) {
    // block options if no cloud license
    return false;
  }
  const zones = getChZones(state, homeId);
  const zoneIds = zones.map(x => x.zoneId);

  let result = true;
  zoneIds.forEach(zone => {
    result = result && canSettingsBeSent(state, homeId, zone);
  });
  return result;
};

export const settingsDisabled = (state, homeId) => {
  const role = getRole(state, homeId);

  if (role === 'Owner') {
    return false;
  }

  return true;
};

export const geolocationEnabled = (state, homeId) => {
  if (!homeId) {
    return '';
  }

  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return '';
  }

  return home.geolocationEnabled;
};

export const awaySetpointEnabled = (state, homeId) => {
  const home = getHomeById(state, homeId);

  return home?.awaySetpointEnabled || false;
};

export const awaySetpoint = (state, homeId) => {
  const home = getHomeById(state, homeId);

  return home?.awaySetpoint;
};

export const geolocationAwayTemperatureAvailable = (state, homeId) => {
  const home = getHomeById(state, homeId);

  return home?.geolocationAwayTemperatureAvailable || false;
};

export const dsrAutoAcceptEnabled = (state, homeId) => {
  if (!homeId) {
    return false;
  }

  const home = getHomeById(state, homeId);

  return home.dsrAutoAccept || false;
};

export const getIsDeviceAtHome = device => {
  return (
    device.currentZone === 0 ||
    (device.previousZone === 0 && device.currentZone === 1)
  );
};

export const getIsDeviceAway = device => {
  return (
    device.currentZone === 2 ||
    (device.previousZone === 2 && device.currentZone === 1)
  );
};

export const getGeolocationDevice = (state, homeId, deviceId) => {
  if (!homeId || !deviceId) {
    return null;
  }
  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return null;
  }

  const foundDevice = home.geolocationDevices.find(
    device => device.id === deviceId,
  );

  return foundDevice;
};

export const getHasAssociatedGeolocationDevice = (state, homeId) => {
  if (!homeId) {
    return null;
  }

  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return null;
  }

  return home.geolocationDevices.length > 0;
};

export const geolocationStateThisDevice = (state, homeId, deviceId) => {
  const foundDevice = getGeolocationDevice(state, homeId, deviceId);

  const isUsing = !!foundDevice;

  if (isUsing) {
    if (getIsDeviceAtHome(foundDevice)) {
      return 'home';
    }
    if (getIsDeviceAway(foundDevice)) {
      return 'away';
    }
  }

  // if (geolocationEnabled(state, homeId)) {
  //   console.log('Geolocation state unknown, device is', foundDevice);
  // }
  return '';
};

export const geolocationState = (state, homeId, zoneId) => {
  if (!homeId) {
    return '';
  }

  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return '';
  }
  const zone = home.zones.find(x => isMatchingZoneId(x.zoneId, zoneId));
  return zone ? zone.geolocationState : null;
};

export const geolocationHomeMode = (state, homeId) => {
  if (!homeId) {
    return { mode: 'disabled', homeCount: 0, awayCount: 0 };
  }

  const home = state.homeList.homes.find(x => x.id === homeId);

  if (!home) {
    return { mode: 'disabled', homeCount: 0, awayCount: 0 };
  }

  if (!home.geolocationEnabled || home.geolocationDevices.length === 0) {
    return { mode: 'disabled', homeCount: 0, awayCount: 0 };
  }

  const mode = home.geolocationDevices.some(x => getIsDeviceAtHome(x))
    ? 'home'
    : 'away';

  const homeCount = home.geolocationDevices.filter(getIsDeviceAtHome).length;
  const awayCount = home.geolocationDevices.filter(getIsDeviceAway).length;

  return {
    mode,
    homeCount,
    awayCount,
  };
};

export const getHeatSource = (state, homeId, zoneId) => {
  const defaultSource = 'Boiler';

  const zones = zoneId
    ? [getZone(state, homeId, zoneId)]
    : getZones(state, homeId);

  if (!zones || zones?.length === 0) {
    return defaultSource;
  }

  for (let i = 0; i < zones.length; i++) {
    const zone = zones[i];

    switch (zone?.type) {
      case 'HPI_CH':
      case 'HPI_DHW':
        return 'Heat pump';

      default:
        return defaultSource;
    }
  }
};

export function getZoneType(state, homeId, zoneId) {
  const zone = getZone(state, homeId, zoneId);
  return zone?.type;
}

export function getChZoneForDhwZone(state, homeId, zoneId) {
  // get all the ch zones
  const zones = getChZones(state, homeId);

  // then get by the physic device id (ignoring endpointId)
  const chZone = zones.find(x => x.zoneId[0] === zoneId[0]);

  return chZone;
}

export const getLiveData = state => {
  return state.homeList.liveData || [];
};

export const isHaloDeviceServiceEnabled = (state, homeId) => {
  const home = getHomeById(state, homeId);

  if (!home) {
    return false;
  }

  // heat pumps always have device service enabled now, but it's not in beta mode
  if (getHeatSource(state, homeId) === 'Heat pump') {
    return true;
  }

  return home.haloDeviceServiceEnabled;
};

export const getIsDsrEnabled = (state, homeId) => {
  const home = getHomeById(state, homeId);

  if (home?.type?.length > 0) {
    const pendingRequest = state.dsr[homeId];

    if (pendingRequest && pendingRequest?.dsrEnabled !== undefined) {
      return pendingRequest.dsrEnabled;
    }

    return home?.hpi?.dsrEnabled;
  } else {
    return false;
  }
};

export const getPendingDsrCommands = state => {
  return state?.pendingDsrCommands?.pendingDsrCommands || [];
};

export const getPendingDsrCommandsCount = state => {
  return state?.pendingDsrCommands?.pendingDsrCommands.length || 0;
};

export const getPendingDsrCommand = (state, id) => {
  return state?.pendingDsrCommands?.pendingDsrCommands.find(
    pendingCommand => pendingCommand.id === id,
  );
};

export const getPartnerAccessRequests = state => {
  return state?.partnerAccessRequests?.partnerAccessRequests || [];
};

export const getPartnerAccessRequestsCount = state => {
  return state?.partnerAccessRequests?.partnerAccessRequests.length || 0;
};

export const getPendingDsrCommandRequestStatus = state => {
  return state?.pendingDsrCommands?.requestStatus;
};

export const getLastPendingDsrCommandRequestId = state => {
  return state?.pendingDsrCommands?.lastNotificationId;
};

export const getServiceModeAlarm = (state, homeId, zoneId) => {
  const zone = getZone(state, homeId, zoneId);
  if (!zone || !zone.serviceModeType) {
    return null;
  }

  const faults = getPrtFaults(state, homeId, zoneId);
  const alarm = faults.find(f => f.type === 'HaloServiceDue');
  return alarm || null;
};

export const getHaloNotificationMessages = state =>
  state.haloNotificationMessages.messages;

export const getHaloNotificationMessagesUnreadCount = state =>
  state.haloNotificationMessages.unreadCount;

export const getHaloNotificationMessageById = (state, id) =>
  state.haloNotificationMessages.messages.find(m => m.id === id);
